<template>
  <!-- Renders NOTHING on a page with no tips, so the sidebar foot stays as it was. -->
  <div v-if="tips.length" class="app-tips" data-test="app-tips">
    <!-- The trigger is the parent's (AppSidebar draws it in its own row / tile style). -->
    <slot :count="tips.length" :open="open" :toggle="toggle" />

    <div
      v-if="open"
      class="app-tips__pop"
      :style="popStyle"
      role="dialog"
      aria-label="Tips for this page"
      data-test="app-tips-panel"
      @keydown.esc.stop.prevent="close"
    >
      <div class="app-tips__head">
        <span class="app-tips__title">Tips for this page</span>
        <button type="button" class="app-tips__close" aria-label="Close tips" data-test="app-tips-close" @click="close">
          <AppIcon name="x" :size="16" />
        </button>
      </div>
      <ul class="app-tips__list">
        <li
          v-for="tip in tips"
          :key="tip.id"
          class="app-tips__item"
          :class="{ 'is-soon': isSoon(tip) }"
          data-test="app-tips-item"
        >
          <span class="app-tips__icon"><i :class="'fa ' + (tip.icon || 'fa-lightbulb-o')"></i></span>
          <div class="app-tips__body">
            <div class="app-tips__item-title">
              {{ tip.title }}
              <span v-if="isSoon(tip)" class="app-tips__soon">Coming soon</span>
            </div>
            <p class="app-tips__text">{{ tip.text || tip.description }}</p>
            <button
              v-if="!isSoon(tip) && tip.ctaRoute"
              type="button"
              class="app-tips__cta"
              data-test="app-tips-cta"
              @click="go(tip)"
            >{{ tip.ctaText || 'Open' }}</button>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
/**
 * App shell — the "Tips for this page" lightbulb (2026-10-13). The page-top tip card (FeatureNudge)
 * pushed the page content down, so tips are now PULLED: a lightbulb beside the bell in the top bar that
 * only exists on a page that has tips, and opens a panel listing them. Nothing slides in on its own and
 * nothing is dismissed.
 *
 * Same source as the old card: the `nudges` store (Firestore `teamio-nudges`, managed in the Super
 * Admin NudgeManager). `nudges/tipsForPage` matches route / role / site like the card does, but does not
 * apply the card's dismissals. "Coming soon" tips stay "Coming soon" with no way to ask for the feature.
 *
 * Renderless trigger: AppTopBar passes its own button through the default scoped slot
 * ({ count, open, toggle }), so it looks like the bell beside it. The panel is position: fixed under the
 * trigger (`placement="below"`; "side" is for a sidebar trigger). Closes on Escape, an outside click and
 * any route change.
 *
 * Defensive on purpose: mounted without a store / router (component tests), it shows nothing.
 */
import AppIcon from './AppIcon.vue';

export default {
  name: 'AppTipsMenu',
  components: { AppIcon },

  props: {
    // 'below': under a top-bar trigger, right edges aligned. 'side': beside a sidebar trigger.
    placement: { type: String, default: 'below', validator: (v) => ['below', 'side'].indexOf(v) > -1 },
  },

  data() {
    return { open: false, popStyle: {} };
  },

  computed: {
    tips() {
      const store = this.$store;
      const route = this.$route;
      const user = store && store.state && store.state.customer && store.state.customer.user;
      if (!store || !route || !user || !store.getters['nudges/tipsForPage']) return [];
      return store.getters['nudges/tipsForPage']({
        route: route.path,
        role: user.Role,
        siteID: user.SiteID || user.siteID,
      });
    },
  },

  watch: {
    '$route.path'() {
      this.close();
    },
    tips(list) {
      if (!list.length) this.close();
    },
  },

  mounted() {
    const store = this.$store;
    if (store && store.state && store.state.nudges && !store.state.nudges.loaded) {
      // The page card used to do this fetch on every page; it is off now, so the menu does it.
      Promise.resolve(store.dispatch('nudges/fetchNudges')).catch(() => { /* tips are optional */ });
    }
  },

  beforeDestroy() {
    this.unlisten();
  },

  methods: {
    isSoon(tip) {
      return tip.type === 'coming_soon';
    },

    toggle() {
      if (this.open) this.close();
      else this.show();
    },

    show() {
      const el = this.$el && this.$el.getBoundingClientRect ? this.$el.getBoundingClientRect() : null;
      if (el && typeof window !== 'undefined') {
        if (this.placement === 'side') {
          // Beside the trigger, growing upward from its bottom edge so the panel never runs off the screen.
          this.popStyle = { left: Math.round(el.right + 12) + 'px', bottom: Math.max(12, Math.round(window.innerHeight - el.bottom)) + 'px' };
        } else {
          // Under the trigger, its right edge on the trigger's right edge (never past the screen edge).
          this.popStyle = { top: Math.round(el.bottom + 8) + 'px', right: Math.max(12, Math.round(window.innerWidth - el.right)) + 'px' };
        }
      }
      this.open = true;
      if (typeof document !== 'undefined') {
        document.addEventListener('mousedown', this.onOutside, true);
        document.addEventListener('keydown', this.onKey, true);
      }
    },

    close() {
      this.open = false;
      this.unlisten();
    },

    unlisten() {
      if (typeof document === 'undefined') return;
      document.removeEventListener('mousedown', this.onOutside, true);
      document.removeEventListener('keydown', this.onKey, true);
    },

    onOutside(e) {
      if (this.$el && !this.$el.contains(e.target)) this.close();
    },

    onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') this.close();
    },

    go(tip) {
      this.close();
      if (this.$router && tip.ctaRoute) this.$router.push(tip.ctaRoute).catch(() => { /* same route */ });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

.app-tips { position: relative; }

.app-tips__pop {
  position: fixed;
  z-index: $shell-z-popover;
  width: 340px;
  max-width: calc(100vw - 24px);
  max-height: min(70vh, 520px);
  display: flex;
  flex-direction: column;
  background: $fu-surface;
  border: 1px solid $fu-border;
  border-radius: 14px;
  box-shadow: $fu-shadow-lg;
  overflow: hidden;
  font-family: $fu-font-sans;
  color: $fu-text;
  animation: app-tips-pop 0.14s ease;
}

@keyframes app-tips-pop {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .app-tips__pop { animation: none; }
}

// Phone: use the full width under the bar with an even gutter (the inline left / right from the trigger
// would otherwise leave the panel hugging one edge).
@media (max-width: 480px) {
  .app-tips__pop {
    left: 12px !important;
    right: 12px !important;
    width: auto;
    max-width: none;
  }
}

.app-tips__head {
  flex: none;
  display: flex;
  align-items: center;
  padding: 12px 10px 10px 16px;
  border-bottom: 1px solid $fu-border;
}
.app-tips__title { font-size: 14px; font-weight: $fu-fw-bold; }
.app-tips__close {
  @include fu-icon-btn(30px);
  margin-left: auto;
}

.app-tips__list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.app-tips__item {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  & + & { border-top: 1px solid $fu-border; }
}

.app-tips__icon {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $fu-brand-50;
  color: $fu-brand-600;
  font-size: 14px;
  .is-soon & { background: #f3efff; color: #7c3aed; }
}

.app-tips__body { min-width: 0; }
.app-tips__item-title { font-size: 13px; font-weight: $fu-fw-bold; line-height: 1.3; }
.app-tips__soon {
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #f3efff;
  color: #6d28d9;
  font-size: 10.5px;
  font-weight: $fu-fw-bold;
  vertical-align: middle;
}
.app-tips__text { margin: 3px 0 0; font-size: 12.5px; line-height: 1.45; color: $fu-neutral-600; }

.app-tips__cta {
  @include fu-link-btn;
  margin-top: 6px;
  padding-left: 0;
  font-size: 12.5px;
  font-weight: $fu-fw-bold;
}
</style>
